import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Send, CheckCircle2, AlertCircle, FileText, UserCheck, RefreshCw } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";

const AdminEmailCenter = () => {
  const { api } = useAdminAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [templateType, setTemplateType] = useState("interview_invitation");
  const [customSubject, setCustomSubject] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const res = await api().get("/admin/applications");
      const list = Array.isArray(res.data) ? res.data : (res.data?.applications || []);
      setCandidates(list);
      if (list.length > 0) setSelectedCandidateId(list[0].id);
    } catch (err) {
      console.error("Failed to load candidates:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, [api]);

  const selectedCand = candidates.find(c => c.id === selectedCandidateId) || candidates[0];

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!selectedCand) return;
    setSending(true);
    setFeedback(null);
    try {
      const res = await api().post(`/admin/applications/${selectedCand.id}/email`, {
        template_type: templateType,
        custom_subject: customSubject,
        custom_body: customBody
      });
      setFeedback({ type: "success", text: res.data?.message || "Email dispatched successfully via Microsoft Graph!" });
      setCustomSubject("");
      setCustomBody("");
      fetchCandidates();
    } catch (err) {
      setFeedback({ type: "error", text: err.response?.data?.detail || "Failed to dispatch email" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-email-center">
      <div className="mb-8">
        <h1 className="text-3xl font-light text-white">Communication & Email Center</h1>
        <p className="text-sm text-cyan-300/60 mt-1">Enterprise candidate correspondence engine with Microsoft Graph integration</p>
      </div>

      {feedback && (
        <div className={`mb-6 p-4 rounded-xl border text-sm flex items-center gap-3 ${feedback.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
          {feedback.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{feedback.text}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Select Candidate & Template */}
        <div className="space-y-6">
          <div className="p-6 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md">
            <h3 className="text-lg font-light text-white mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-cyan-400" /> Select Recipient
            </h3>
            {loading ? (
              <div className="text-white/40 text-sm">Loading candidate directory...</div>
            ) : (
              <select
                value={selectedCandidateId}
                onChange={(e) => setSelectedCandidateId(e.target.value)}
                className="w-full p-3 rounded-xl border border-white/10 bg-black/60 text-sm text-white focus:outline-none focus:border-cyan-400/50"
              >
                {candidates.map((c) => (
                  <option key={c.id} value={c.id} className="bg-slate-900 text-white">
                    {c.full_name} ({c.position_title || c.job_role || "Role"})
                  </option>
                ))}
              </select>
            )}

            {selectedCand && (
              <div className="mt-4 p-4 rounded-xl bg-black/40 border border-white/5 text-xs text-white/70 space-y-1">
                <div><span className="text-cyan-300/70">Email:</span> {selectedCand.email}</div>
                <div><span className="text-cyan-300/70">Ref ID:</span> {selectedCand.application_id || selectedCand.reference_number}</div>
                <div><span className="text-cyan-300/70">Department:</span> {selectedCand.department || "General"}</div>
                <div><span className="text-cyan-300/70">Status:</span> <span className="uppercase text-cyan-300">{selectedCand.status}</span></div>
              </div>
            )}
          </div>

          <div className="p-6 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md">
            <h3 className="text-lg font-light text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-400" /> Communication Template
            </h3>
            <div className="space-y-2">
              {[
                { id: "interview_invitation", label: "Interview Invitation" },
                { id: "offer_letter", label: "Official Offer Letter" },
                { id: "rejection", label: "Rejection Notice" },
                { id: "assessment", label: "Quantitative Assessment" },
                { id: "custom", label: "Custom Message" }
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTemplateType(t.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl text-xs font-medium uppercase tracking-wider transition-all border ${
                    templateType === t.id
                      ? "bg-cyan-400/20 text-cyan-300 border-cyan-400/40"
                      : "bg-white/5 text-white/60 border-white/5 hover:border-white/10"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column: Composer */}
        <div className="lg:col-span-2 p-6 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md">
          <h3 className="text-lg font-light text-white mb-6 flex items-center gap-2">
            <Mail className="w-5 h-5 text-cyan-400" /> Email Composer
          </h3>

          <form onSubmit={handleSendEmail} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-cyan-300/60 mb-2">Subject Line (Optional Custom Subject)</label>
              <input
                type="text"
                placeholder="Leave blank to use template default subject..."
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                className="w-full p-3 rounded-xl border border-white/10 bg-black/60 text-sm text-white focus:outline-none focus:border-cyan-400/50"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-cyan-300/60 mb-2">Message Body (Supports Variables: &#123;&#123;CandidateName&#125;&#125;, &#123;&#123;ReferenceID&#125;&#125;, &#123;&#123;Position&#125;&#125;)</label>
              <textarea
                rows={10}
                placeholder="Leave blank to use pre-built template text, or type a custom body..."
                value={customBody}
                onChange={(e) => setCustomBody(e.target.value)}
                className="w-full p-3 rounded-xl border border-white/10 bg-black/60 text-sm text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/50 font-mono"
              />
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={sending || !selectedCand}
                className="px-6 py-3 rounded-xl font-medium text-sm text-black flex items-center gap-2 hover:opacity-90 disabled:opacity-50 transition-all"
                style={{ background: "linear-gradient(135deg, #00FFD1 0%, #00a884 100%)" }}
              >
                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                <span>{sending ? "Dispatching Email..." : "Dispatch Email via Microsoft Graph"}</span>
              </button>
            </div>
          </form>

          {/* Candidate Email History */}
          {selectedCand?.email_history && selectedCand.email_history.length > 0 && (
            <div className="mt-8 border-t border-white/10 pt-6">
              <h4 className="text-sm font-medium text-white mb-4">Past Communication History</h4>
              <div className="space-y-3">
                {selectedCand.email_history.map((h, i) => (
                  <div key={i} className="p-3 rounded-xl bg-black/40 border border-white/5 text-xs text-white/70">
                    <div className="flex justify-between text-cyan-300 mb-1">
                      <span>{h.subject}</span>
                      <span className="text-white/40">{new Date(h.sent_at).toLocaleString()}</span>
                    </div>
                    <p className="line-clamp-2 text-white/60">{h.body}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEmailCenter;
